import React, { useState, useEffect } from 'react';
import {
  Table,
  Input,
  Button,
  message,
  Select,
  Tag,
  Modal,
  Form,
  Row,
  Col,
} from 'antd';
import {
  FilterOutlined,
  SearchOutlined,
  CaretUpOutlined,
  CaretDownOutlined,
  VerticalAlignBottomOutlined,
} from '@ant-design/icons';
import ChargingHttpHandler from '../../../shared/chargingHttpHandler';
import secureStorage from '../../../shared/secureStorage';
import dayjs from 'dayjs';

const { Option } = Select;

const CustomerTab = () => {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [crFilter, setCrFilter] = useState('all'); // 'all', 'cr', 'non-cr'
  const [filterVisible, setFilterVisible] = useState(false);
  const [filters, setFilters] = useState({});
  const [filteredData, setFilteredData] = useState([]);
  const [form] = Form.useForm();
  const [sortedInfo, setSortedInfo] = useState({});

  const httpHandler = new ChargingHttpHandler(false);
  const chargingAccessToken = secureStorage.getItem('chargingAccessToken');

  // 手機號碼隱碼：隱藏第5、6、7位
  const maskPhoneNumber = (phone) => {
    if (!phone || phone === '未知' || phone.length < 7) return phone;
    return phone.substring(0, 4) + '***' + phone.substring(7);
  };

  // 名字隱碼 只留第一個字母就好
  const maskName = (name) => {
    if (!name || name === '未知' || name.length === 0) return name;
    const firstChar = name.charAt(0);
    const maskedPart = '*'.repeat(name.length - 1);
    return firstChar + maskedPart;
  };

  // 電子郵件隱碼
  const maskEmail = (email) => {
    if (!email || email === '未知' || !email.includes('@')) return email;
    const [localPart, domain] = email.split('@');
    if (localPart.length <= 1) return email;
    const firstChar = localPart.charAt(0);
    const maskedPart = '*'.repeat(localPart.length - 1);
    return firstChar + maskedPart + '@' + domain;
  };

  // 車號隱碼：保留前2位和後2位，中間用*
  const maskCarNumber = (carNumber) => {
    if (
      !carNumber ||
      carNumber === '未知' ||
      carNumber === '無' ||
      carNumber.length < 4
    )
      return carNumber;
    const firstTwo = carNumber.substring(0, 2);
    const lastTwo = carNumber.substring(carNumber.length - 2);
    const middleLength = carNumber.length - 4;
    const maskedMiddle = '*'.repeat(middleLength);
    return firstTwo + maskedMiddle + lastTwo;
  };

  const add_search_log = async (success) => {
    const userRole = secureStorage.getItem('userRole');
    // 紀錄搜尋時間
    await httpHandler
      .axios()
      .post(
        '/platform/update_user_log_table',
        {
          log_time: dayjs().format('YYYY/MM/DD HH:mm:ss'),
          user_role: userRole,
          action_type: '客戶搜尋',
          action_content: '用戶搜尋客戶資訊',
          status: success,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${chargingAccessToken}`,
          },
        }
      )
      .catch((e) => {
        console.error('寫入搜尋紀錄失敗', e);
      });
  };

  const fetchMemberInfo = async () => {
    try {
      setLoading(true);

      // 同時獲取會員資料和訂單資料
      const [memberResponse, orderResponse] = await Promise.all([
        httpHandler.axios().get('/platform/get_member_info', {
          headers: {
            Authorization: `Bearer ${chargingAccessToken}`,
          },
        }),
        httpHandler.axios().get('/platform/all-cdr-info', {
          headers: {
            Authorization: `Bearer ${chargingAccessToken}`,
          },
        }),
      ]);

      const memberData = memberResponse.data.data;
      const orderData = orderResponse.data.data || [];

      if (memberData) {
        await add_search_log(1);
      } else {
        await add_search_log(0);
      }

      const orderStatsMap = {};
      orderData.forEach((order) => {
        const accountId = order.userId; // 使用 userId 來關聯會員
        if (!accountId) return; // 跳過沒有 userId 的訂單

        if (!orderStatsMap[accountId]) {
          orderStatsMap[accountId] = {
            chargeCount: 0,
            totalKwh: 0,
            totalAmount: 0,
          };
        }
        orderStatsMap[accountId].chargeCount += 1;
        orderStatsMap[accountId].totalKwh += parseFloat(order.chargedKwh || 0);
        orderStatsMap[accountId].totalAmount += parseFloat(
          order.totalAmount || 0
        );
      });

      const transformedData = memberData.map((item, index) => {
        const stats = orderStatsMap[item.account] || {
          chargeCount: 0,
          totalKwh: 0,
          totalAmount: 0,
        };

        return {
          key: index.toString(),
          id: item.account,
          name: item.name,
          phone: item.phone,
          email: item.email,
          appType: 'EasyRent', // 應用商先名稱一律為 EasyRent 不確定這個到底抓不抓得到
          carCount: item.car_number ? 1 : 0, // 目前都只會有一台 不確定未來會不會開放多台
          carNumber: item.car_number || '無', // 車號
          crCheck: item.cr_check || 0,
          isCR: item.cr_check === 1, // 判斷是否為 CR 用戶
          chargeCount: stats.chargeCount, // 充電次數
          totalKwh: stats.totalKwh, // 總度數
          totalAmount: stats.totalAmount, // 總金額
        };
      });

      setData(transformedData);
      setFilteredData(transformedData);
    } catch (error) {
      console.error('Failed to fetch member info:', error);
      message.error('獲取客戶資料失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMemberInfo();
  }, []);

  // 應用篩選邏輯
  const applyFilters = (values) => {
    let filtered = [...data];

    // 客戶ID篩選
    if (values.id && values.id.trim()) {
      filtered = filtered.filter((item) =>
        item.id.toLowerCase().includes(values.id.toLowerCase())
      );
    }

    // 姓名篩選
    if (values.name && values.name.trim()) {
      filtered = filtered.filter((item) =>
        item.name.toLowerCase().includes(values.name.toLowerCase())
      );
    }

    // 電話篩選
    if (values.phone && values.phone.trim()) {
      filtered = filtered.filter((item) => item.phone.includes(values.phone));
    }

    // 電子郵件篩選
    if (values.email && values.email.trim()) {
      filtered = filtered.filter((item) =>
        item.email.toLowerCase().includes(values.email.toLowerCase())
      );
    }

    // 應用商名稱篩選
    if (values.appType && values.appType.trim()) {
      filtered = filtered.filter((item) =>
        item.appType.toLowerCase().includes(values.appType.toLowerCase())
      );
    }

    // 車號篩選
    if (values.carNumber && values.carNumber.trim()) {
      filtered = filtered.filter((item) =>
        item.carNumber.toLowerCase().includes(values.carNumber.toLowerCase())
      );
    }

    // CR用戶篩選
    if (values.isCR !== undefined && values.isCR !== '') {
      const isCR = values.isCR === 'cr';
      filtered = filtered.filter((item) => item.isCR === isCR);
    }

    return filtered;
  };

  const handleAdvancedFilter = (values) => {
    const result = applyFilters(values);
    setFilteredData(result);
    setFilters(values);
    setFilterVisible(false);
    setSortedInfo({}); // 清除排序狀態
    message.success(`篩選完成，共 ${result.length} 筆資料`);
  };

  // 清除篩選
  const clearFilters = () => {
    setFilteredData(data);
    setFilters({});
    form.resetFields();
    setSearch('');
    setCrFilter('all');
    setSortedInfo({});
  };

  const columns = [
    {
      title: '客戶ID',
      dataIndex: 'id',
      render: (text) => (
        <span
          style={{
            color: '#1890ff',
            background: '#f0f7ff',
            padding: '2px 8px',
            borderRadius: 4,
          }}>
          {text}
        </span>
      ),
    },
    {
      title: '姓名',
      dataIndex: 'name',
      render: (name) => maskName(name),
    },
    {
      title: '電話',
      dataIndex: 'phone',
      render: (phone) => maskPhoneNumber(phone),
    },
    {
      title: '電子郵件',
      dataIndex: 'email',
      render: (email) => maskEmail(email),
    },
    {
      title: '應用商名稱',
      dataIndex: 'appType',
      width: 120,
    },
    {
      title: '擁有車輛數',
      dataIndex: 'carCount',
      width: 120,
      render: (count) => count || 0,
    },
    {
      title: '車號',
      dataIndex: 'carNumber',
      width: 120,
      render: (carNumber) => maskCarNumber(carNumber),
    },
    {
      title: () => (
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            cursor: 'pointer',
          }}
          onClick={() => handleSort('chargeCount')}>
          充電次數
          {renderSortIcon('chargeCount')}
        </span>
      ),
      dataIndex: 'chargeCount',
      key: 'chargeCount',
      width: 120,
      render: (count) => (
        <span style={{ fontWeight: 'bold', color: '#1890ff' }}>{count}</span>
      ),
    },
    {
      title: () => (
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            cursor: 'pointer',
          }}
          onClick={() => handleSort('totalKwh')}>
          總度數(kWh)
          {renderSortIcon('totalKwh')}
        </span>
      ),
      dataIndex: 'totalKwh',
      key: 'totalKwh',
      width: 140,
      render: (kwh) => (
        <span style={{ fontWeight: 'bold', color: '#52c41a' }}>
          {kwh.toFixed(2)}
        </span>
      ),
    },
    {
      title: () => (
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            cursor: 'pointer',
          }}
          onClick={() => handleSort('totalAmount')}>
          總金額
          {renderSortIcon('totalAmount')}
        </span>
      ),
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 120,
      render: (amount) => (
        <span style={{ fontWeight: 'bold', color: '#fa8c16' }}>
          NT$ {amount.toFixed(0)}
        </span>
      ),
    },
    {
      title: '用戶類型',
      dataIndex: 'isCR',
      render: (isCR) => (
        <Tag color={isCR ? 'green' : 'blue'}>
          {isCR ? 'CR用戶' : '一般用戶'}
        </Tag>
      ),
    },
  ];

  // 下載客戶名單
  const handleDownload = () => {
    httpHandler
      .fileDownload(`/platform/download_member_info`, 'csv', '客戶名單', {
        Authorization: `Bearer ${chargingAccessToken}`,
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // 簡單搜尋功能（保留原有的搜尋框功能）
  const handleSearch = () => {
    setSortedInfo({}); // 清除排序狀態
    if (!search.trim()) {
      setFilteredData(data);
      return;
    }
    const filtered = data.filter(
      (item) =>
        item.id.toLowerCase().includes(search.toLowerCase()) ||
        item.name.toLowerCase().includes(search.toLowerCase()) ||
        item.phone.toLowerCase().includes(search.toLowerCase())
    );
    setFilteredData(filtered);
  };

  // 手動處理排序
  const handleSort = (columnKey) => {
    let newOrder = 'ascend';

    // 如果點擊的是當前排序欄位，則切換排序方向
    if (sortedInfo.columnKey === columnKey) {
      if (sortedInfo.order === 'ascend') {
        newOrder = 'descend';
      } else if (sortedInfo.order === 'descend') {
        // 取消排序，回到原始順序
        setSortedInfo({});
        // 根據當前的篩選條件重新應用篩選，恢復初始順序
        if (Object.keys(filters).length > 0) {
          const result = applyFilters(filters);
          setFilteredData(result);
        } else {
          setFilteredData(data);
        }
        return;
      }
    }

    // 設置新的排序狀態
    setSortedInfo({ columnKey, order: newOrder });

    // 執行排序
    const sorted = [...filteredData].sort((a, b) => {
      const aValue = a[columnKey];
      const bValue = b[columnKey];

      if (newOrder === 'ascend') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });

    setFilteredData(sorted);
  };

  // 自定義排序圖標
  const renderSortIcon = (columnKey) => {
    if (sortedInfo.columnKey === columnKey) {
      // 有排序時顯示對應的箭頭，藍色
      if (sortedInfo.order === 'ascend') {
        return <CaretUpOutlined style={{ color: '#1890ff', fontSize: 14 }} />;
      } else if (sortedInfo.order === 'descend') {
        return <CaretDownOutlined style={{ color: '#1890ff', fontSize: 14 }} />;
      }
    }
    // 沒有排序時顯示向下箭頭，灰色
    return <CaretDownOutlined style={{ color: '#d9d9d9', fontSize: 14 }} />;
  };

  return (
    <div
      style={{
        background: '#f7faf9',
        padding: 15,
        borderRadius: 16,
        marginTop: 24,
      }}>
      <div
        style={{
          display: 'flex',
          gap: 8,
          marginBottom: 16,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}>
        <Input
          placeholder="搜尋客戶ID、姓名或電話"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onPressEnter={handleSearch}
          style={{ maxWidth: 350 }}
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
          搜尋
        </Button>
        <Button icon={<VerticalAlignBottomOutlined />} onClick={handleDownload}>
          下載客戶名單
        </Button>
        <Button
          icon={<FilterOutlined />}
          onClick={() => setFilterVisible(true)}>
          篩選
        </Button>
        <Button onClick={clearFilters}>清除</Button>
      </div>

      {/* 篩選條件顯示 */}
      {Object.keys(filters).length > 0 && (
        <div style={{ marginBottom: 12, fontSize: 12, color: '#666' }}>
          篩選條件:
          {Object.entries(filters)
            .filter(([key, value]) => value && value.toString().trim() !== '')
            .map(([key, value]) => {
              const fieldLabels = {
                id: '客戶ID',
                name: '姓名',
                phone: '電話',
                email: '電子郵件',
                appType: '應用商名稱',
                carNumber: '車號',
                isCR: '用戶類型',
              };
              const fieldLabel = fieldLabels[key] || key;
              return (
                <Tag
                  key={key}
                  closable
                  onClose={() => {
                    const newFilters = { ...filters };
                    delete newFilters[key];
                    setFilters(newFilters);
                    const result = applyFilters(newFilters);
                    setFilteredData(result);
                  }}>
                  {fieldLabel}
                </Tag>
              );
            })}
        </div>
      )}

      <div style={{ marginBottom: 16, fontSize: 14, color: '#666' }}>
        共 {filteredData.length} 筆資料
      </div>
      <Table
        columns={columns}
        dataSource={filteredData}
        pagination={{
          pageSize: 10,
          showSizeChanger: false,
          showTotal: (total, range) =>
            `第 ${range[0]}-${range[1]} 筆，共 ${total} 筆`,
          position: ['bottomCenter'],
        }}
        bordered={false}
        loading={loading}
        style={{ background: '#fff', borderRadius: 12 }}
        scroll={{ x: 1200 }}
      />

      {/* 進階篩選 Modal */}
      <Modal
        title="進階篩選"
        open={filterVisible}
        onCancel={() => setFilterVisible(false)}
        width={800}
        footer={[
          <Button
            key="clear"
            onClick={() => {
              form.resetFields();
              setFilters({});
              setFilteredData(data);
            }}>
            清除所有
          </Button>,
          <Button key="cancel" onClick={() => setFilterVisible(false)}>
            取消
          </Button>,
          <Button key="search" type="primary" onClick={() => form.submit()}>
            搜尋
          </Button>,
        ]}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAdvancedFilter}
          initialValues={filters}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="客戶ID" name="id">
                <Input placeholder="客戶ID" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="姓名" name="name">
                <Input placeholder="姓名" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="電話" name="phone">
                <Input placeholder="電話" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="電子郵件" name="email">
                <Input placeholder="電子郵件" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="應用商名稱" name="appType">
                <Input placeholder="應用商名稱" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="車號" name="carNumber">
                <Input placeholder="車號" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="用戶類型" name="isCR">
                <Select placeholder="請選擇">
                  <Select.Option value="cr">CR用戶</Select.Option>
                  <Select.Option value="non-cr">一般用戶</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default CustomerTab;
